import * as React from 'react';
import  { useRef } from 'react';
import classNames from 'classnames';
import _uniqueId from 'lodash/uniqueId';

import { domToReact, htmlToDOM } from 'html-react-parser';
import { Popover, PopoverInteractionKind, Classes } from '@blueprintjs/core';
import { useTimeout } from '../../../../Utilities/Generic';

import MenuItem from '../../../../Components/MenuItem';
import Overlay from '../../../../Components/Overlay';
import Icon from '../../../../Components/Icon';
import AccessibleDrop from '../../../../Components/AccessibleDrop';
import NestedMenu from '../../../../Components/NestedMenu';
import NestedMenuWrapper from '../../../../Components/NestedMenuWrapper';
import NestedMenuItem from '../../../../Components/NestedMenuItem';
import NestedMenuItemHeader from '../../../../Components/NestedMenuItemHeader';
import PortalPopover from '../../../../Components/PortalPopover';

export interface ISiteMenuProps {
    /* DOM to parse */
    dom: string | object[];
    /** Open mobile menu event */
    openMobileMenu: (e) => void;
    /** Close mobile menu event */
    closeMobileMenu: (e) => void;
    /** Should mobile menu be open */
    mobileMenuOpen: boolean;
    /** Popover target to render popovers */
    target?: string;
    /** Mode of menu in mobile devices - overlay or collapse */
    mobileMode?: string;
    /** Popover target for mobile devices */
    mobileTarget?: string;
    /** Theme to use in classes */
    theme?: string;
    /** Labels to use in mobile menu */
    labelBack: string;
    labelViewAll: string;
    iconDrop: string;
    componentMapper: (maybeComponent: string, maybeContent: string, maybeConfig) => React.Component;
    interactionMode: PopoverInteractionKind;
}

interface IMenuItem {
    inner: any; // TODO: fix!
    children?: IMenuItem[];
}

const SiteMenu: React.FunctionComponent<ISiteMenuProps> = (props) => {

    const target = props.target && document.getElementById(props.target) || null;
    const mobileTarget = props.mobileTarget && document.getElementById(props.mobileTarget) || document.body;
    const theme = props.theme ? props.theme : 'ideal';
    const [dropContent, setDropContent] = React.useState<React.ReactNode>(null);
    const [activeElement, setActiveElement] = React.useState<any | null>(null);
    const [subDropContent, setSubDropContent] = React.useState<React.ReactNode>(null);
    const [dropOpen, setDropOpen] = React.useState<React.ReactNode>(null);
    const [timer, setTimer] = React.useState<number | null>(null);
    const [activeElementTimer, setActiveElementTimer] = React.useState<number | null>(null);
    const [headerHeight, setHeaderHeight] = React.useState<number>(0);

    const setActiveElements = (val) => {
        const el = document.body;
        if(el){
            if(val) {
                setActiveElementTimer(null);
                el.classList.add('has-menu');
            } else {
                setActiveElementTimer(100);
            }
        }
    };

    const toggleMobileMenu = (e) => {
        const doc = document.documentElement;
        if (props.mobileMenuOpen) {
            props.closeMobileMenu(e);
            if (doc) {
                doc.classList.remove('mobilemenuopen');
            }
        } else {
            props.openMobileMenu(e);
            if (doc) {
                doc.classList.add('mobilemenuopen');
            }
        }

    };

    useTimeout(() => {
        const el = document.body;
        setActiveElementTimer(null);
        el.classList.remove('has-menu');
    }, activeElementTimer);

    useTimeout(() => {
        setTimer(null);
        setDropOpen(false);
    }, timer);

    const parserSubmenuOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'a' && !!domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    const subitems = domNode.next.name==='ul' ? (
                        <ul id="submenu">{domToReact(domNode.next.children,{})}</ul>
                    ) : (
                        <div>{domToReact(domNode.next.children,{})}</div>
                    );
                    const inner = domToReact(domNode.children, parserOptions);
                    const id = domNode.children;
                    return (
                        <a
                            className={classNames(className, activeElement===id && 'active')}
                            {...rest}
                            tabIndex={0}
                            onClick={(e) => {
                                e.preventDefault();
                                setSubDropContent(subitems);
                                setActiveElement(id);
                            }}
                            onKeyDown={(e) => {
                                if(e.key==='ArrowRight'){
                                    const submenu = document.getElementById('submenu');
                                    if(submenu) {
                                        const subitem = submenu.getElementsByTagName('a')[0];
                                        if(subitem) { subitem.focus(); }
                                    }
                                }
                            }}
                        >{inner}<Icon kind="arrow5-right" width={16} height={16}/></a>
                    );
                }
                if (domNode.name === 'ul' || domNode.name === 'div'){
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    const parserCustomContent = {
        replace: (domNode: any) => {
            if (domNode.name) {
                const { class: className, ...rest } = domNode.attribs;
                if (domNode.attribs['data-subcomponent']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-subcomponent'];
                    const maybeContent = domNode.children ?? '';
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                } else if (domNode.attribs['data-component']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-component'];
                    const maybeContent = domNode.children ?? '';
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                } else {
                    return(
                        <domNode.name className={className} {...rest}>
                            {domToReact(domNode.children, {})}
                        </domNode.name>
                    );
                }
            }
            return false;
        },
    };

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.attribs['data-subcomponent']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-subcomponent'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }
                if (domNode.attribs['data-component']){
                    const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                    const maybeComponent = domNode.attribs['data-component'];
                    const maybeContent = domNode.children ?? '';
                    const { class: className, ...rest } = domNode.attribs;
                    const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                    if (mappedComponent) {
                        return (
                            <domNode.name className={className} {...rest}>
                                {mappedComponent}
                            </domNode.name>
                        );
                    }
                }
                if (domNode.name === 'nav') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <nav
                            {...rest}
                            className={className}
                        >
                            {domToReact(domNode.children, parserOptions)}
                        </nav>
                    );
                }
                if (domNode.name === 'div' && domNode.attribs.class==='site-menu__wrapper'){
                    const inner = domToReact(domNode.children, parserOptions);
                    return (
                        <React.Fragment>
                            {inner}
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'li' && domNode.attribs.class?.split(' ').includes('desktop-hide')) {
                    return <></>;
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const nextIsMultiGroup = domNode.next.attribs.class?.split(' ').includes('multigroup') || false;
                    const nextIsSingleGroup = domNode.next.attribs.class?.split(' ').includes('singlegroup') || false;
                    const nextIsHTMLContent = domNode.next.attribs.class?.split(' ').includes('custom') || false;
                    const nextIsSingle = domNode.next.attribs.class?.split(' ').includes('single') || false;


                    if (isMenuToggler) {
                        if (props.mobileMode==='overlay') {
                            return (
                                <a
                                    {...rest}
                                    className={className}
                                    onClick={props.openMobileMenu}
                                >{inner}</a>
                            );
                        } else {
                            return (
                                <PortalPopover
                                    portalClassName={classNames('site-menu-portal','main-nav')}
                                    popoverClassName={classNames('site-menu-popover')}
                                    portalContainer={mobileTarget}
                                    isOpen={props.mobileMenuOpen}
                                    onClose={(e) => {
                                        props.closeMobileMenu(e);
                                        const doc = document.documentElement;
                                        doc.classList.remove('mobilemenuopen');
                                    }}
                                    content={(
                                        <React.Fragment>
                                            {domToReact(domTree, parserOptionsMobile)}
                                        </React.Fragment>
                                    )}
                                    target={(
                                        <a
                                            {...rest}
                                            className={classNames(className, props.mobileMenuOpen && 'active')}
                                            onClick={(e) => {
                                                toggleMobileMenu(e);
                                                const evt = document.createEvent('UIEvents');
                                                evt.initEvent('resize', true, false);
                                                window.dispatchEvent(evt);
                                            }}
                                        >{inner}</a>
                                    )}
                                />
                            );
                        }

                    }

                    if(target && (nextIsMultiGroup || nextIsSingleGroup || nextIsHTMLContent)) {
                        return (
                            <Popover
                                enforceFocus={true}
                                interactionKind={props.interactionMode}
                                position={'bottom-left'}
                                usePortal={true}
                                minimal={true}
                                autoFocus={true}
                                boundary={'window'}
                                modifiers={{
                                    offset: {
                                        offset: '0, 0',
                                    },
                                    flip: {
                                        enabled: false,
                                    },
                                }}
                                captureDismiss={true}
                                portalClassName={classNames('site-menu-portal','main-nav')}
                                popoverClassName={classNames('site-menu-popover')}
                                portalContainer={target}
                                onOpened={(el) => {
                                    const menu = document.getElementsByClassName('site-menu__drop__nav')[0];
                                    if(menu) {
                                        const item = menu.getElementsByTagName('a')[0];
                                        if(item) { item.click(); }
                                    }
                                    const header = document.getElementById('main-header');
                                    if (header) {
                                        const headerRect = header.getBoundingClientRect();
                                        let h = headerRect.top + headerRect.height;
                                        const cookie = document.getElementById('cookiemessage');
                                        if(cookie) {
                                            const cookieRect = cookie.getBoundingClientRect();
                                            h+=cookieRect.height;
                                        }
                                        setHeaderHeight(h);
                                    }
                                    setActiveElements(true);
                                }}
                                onClosed={() => {
                                    setActiveElements(false);
                                }}
                                content={(
                                    <div className={classNames('site-menu__drop',Classes.POPOVER_DISMISS)} style={{height: 'calc(100vh - ' + headerHeight +'px )'}}>
                                        <div className={classNames('site-menu__drop__limiter',Classes.POPOVER_DISMISS_OVERRIDE )}>
                                            {nextIsMultiGroup ? (
                                                <React.Fragment>
                                                    <ul className="site-menu__drop__nav">
                                                        {domToReact(domNode.next.children, parserSubmenuOptions)}
                                                    </ul>
                                                    <div className="site-menu__drop__content">
                                                        {subDropContent}
                                                    </div>
                                                </React.Fragment>
                                            ) : nextIsSingleGroup ? (
                                                <div className="site-menu__drop__content">
                                                    <ul>{domToReact(domNode.next.children, {})}</ul>
                                                </div>
                                            ) : (
                                                <div className="site-menu__drop__content">
                                                    {domToReact(domNode.next.children, parserCustomContent)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                                target={(
                                    <a
                                        {...rest}
                                        className={className}
                                        suppressHydrationWarning={true}
                                        onClick={(e) => {
                                            if(domNode.attribs.href==='#' && props.interactionMode==='hover'){
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <span className="toggle-icon"><img src={props.iconDrop} alt=""/></span>{inner}
                                    </a>
                                )}
                                transitionDuration={0}
                                hoverOpenDelay={0}
                                hoverCloseDelay={100}
                            />
                        );
                    } else if(nextIsSingle) {
                        return (
                            <AccessibleDrop dom={domNode.parent.children} config={
                                {
                                    'offset':'0, 0',
                                    'position': 'bottom-left',
                                    'enforceFocus':true,
                                    'boundary': 'window',
                                    'autoFocus': true,
                                    'fill': true,
                                    'popoverClassName':'site-menu-popover-single',
                                    'arrow': props.iconDrop,
                                    'setOpen': setActiveElements,
                                    'interactionMode': props.interactionMode,
                                }
                            }/>
                        );
                    } else {
                        return (
                            <a
                                {...rest}
                                className={classNames('bp3-menu-item', className)}
                                suppressHydrationWarning={true}
                                onClick={(e) => { e.preventDefault(); }}
                            >
                                {inner}
                            </a>
                        );
                    }
                }

                if (domNode.name === 'div' && domNode.attribs.class?.split(' ').includes('custom')) {
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    const parserSubOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {

                if (domNode.name === 'li') {
                    const { class: className } = domNode.attribs;
                    const isDesktopHide = className?.split(' ').includes('desktop-hide') || false;
                    return (
                        <React.Fragment>
                            {!isDesktopHide ?? domToReact(domNode.children, parserSubOptions)}
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next && domNode.parent.name !== 'p') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        >
                            {/* domToReact(domNode.next.children, parserSubOptions) */}
                        </MenuItem>
                    );
                }

                if (domNode.name === 'a' && !domNode.next && domNode.parent.name !== 'p') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        />
                    );
                }

                if (domNode.name === 'ul') {
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    const parserOptionsMobile = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'nav') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <nav
                            {...rest}
                            className={className}
                        >
                            {domToReact(domNode.children, parserOptionsMobile)}
                        </nav>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptionsMobile);
                    const { class: className } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const isDefaultOpen = className?.split(' ').includes('open') || false;
                    const isActive = className?.split(' ').includes('active') || false;
                    if (isMenuToggler) {
                        return (
                            <React.Fragment/>
                        );
                    }
                    return (
                        <NestedMenuItemHeader isActive={isActive} isDefaultOpen={isDefaultOpen} labelBack={props.labelBack} labelViewAll={props.labelViewAll} className={className} link={domNode.attribs.href}>
                            {inner}
                        </NestedMenuItemHeader>
                    );
                }

                if (domNode.name === 'ul') {
                    const inner = domToReact(domNode.children, parserOptionsMobile);
                    if(!!domNode.next) {
                        return (
                            <NestedMenu>
                                {inner}
                                <NestedMenuItem className="banner">{domToReact(domNode.next.children, parserOptionsMobile)}</NestedMenuItem>
                            </NestedMenu>
                        );
                    } else {
                        return (
                            <NestedMenuWrapper level={domNode.parent && domNode.parent.name==='nav' ? 'main' : 'sub'}>
                                <NestedMenu>
                                    {inner}
                                </NestedMenu>
                            </NestedMenuWrapper>
                        );
                    }
                }

                if (domNode.name === 'div') {

                    if(domNode.attribs.class!=='banner') {
                        if (domNode.attribs['data-subcomponent']){
                            const maybeConfig = JSON.parse(domNode.attribs['data-config'] || '{}');
                            const maybeComponent = domNode.attribs['data-subcomponent'];
                            const maybeContent = domNode.children ?? '';
                            const { class: className, ...rest } = domNode.attribs;
                            const mappedComponent = props.componentMapper(maybeComponent, maybeContent, maybeConfig);

                            if (mappedComponent) {
                                return (
                                    <domNode.name className={className} {...rest}>
                                        {mappedComponent}
                                    </domNode.name>
                                );
                            }
                        } else {
                            const inner = domToReact(domNode.children, parserOptionsMobile);
                            return (
                                <NestedMenuWrapper level={domNode.parent && domNode.parent.name==='nav' ? 'main' : 'sub'}>
                                    {inner}
                                </NestedMenuWrapper>
                            );
                        }
                    } else {
                        return (
                            <React.Fragment/>
                        );
                    }
                }

                if (domNode.name === 'li') {
                    const { class: className, id } = domNode.attribs;
                    const inner = className==='static' ? domToReact(domNode.children,{}) : domToReact(domNode.children, parserOptionsMobile);
                    if(className==='static') {
                        return(<li className={classNames('nested-menu__item', className)} id={id}>{inner}</li>);
                    } else {
                        return (
                            <NestedMenuItem className={className} id={id}>
                                {inner}
                            </NestedMenuItem>
                        );
                    }
                }
            }
            return false;
        },
    };

    // Main output

    const domTree = Array.isArray(props.dom) ? props.dom : htmlToDOM(props.dom.replace(/>\s+</g, '><'));
    return (
        <React.Fragment>
            {domToReact(domTree, parserOptions)}
            {props.mobileMode === 'overlay' && (
                <Overlay
                    isOpen={props.mobileMenuOpen}
                    doClose={props.closeMobileMenu}
                    layout="focusview"
                    size="max"
                    className="site-menu-mobile"
                    customHeader={(
                        <div className="site-menu-mobile-heading right">
                            <button onClick={props.closeMobileMenu}>
                                <Icon kind="close03" width={16} height={16}/>
                            </button>
                        </div>
                    )}
                >
                    <div className="site-menu-mobile-content">
                        {
                            domToReact(domTree, parserOptionsMobile)
                        }
                    </div>
                </Overlay>
            )}
        </React.Fragment>
    );
};

SiteMenu.displayName = 'SiteMenu';

export default SiteMenu;
